/**
 * Single resolver for Naturals HQ/partner/salon-admin API base URL.
 * Never use /salon-admin-api or /enterprise-salon-admin-api under /naturals/* routes.
 */
const DEFAULT_DEV_API_ORIGIN = 'http://localhost:5113';
const NATURALS_ADMIN_PATH = '/naturals-salon-admin-api';

function isLocalHostname(host) {
  const h = String(host || '').toLowerCase();
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '[::1]' ||
    h.endsWith('.local')
  );
}

function normalizeApiBase(value) {
  if (!value || !String(value).trim()) return '';
  return String(value).trim().replace(/\/$/, '');
}

/** Reject Nyafaz / enterprise salon-admin bases when resolving Naturals. */
export function isWrongSalonAdminApi(url) {
  const text = String(url || '').toLowerCase();
  if (!text) return false;
  if (text.includes('/enterprise-salon-admin-api')) return true;
  if (text.includes('/salon-admin-api') && !text.includes('/naturals-salon-admin-api')) {
    return true;
  }
  if (/:5003(\/|$)/.test(text)) return true;
  if (/:5013(\/|$)/.test(text)) return true;
  return false;
}

function productionNaturalsApiOriginForHost(origin) {
  try {
    const url = new URL(origin);
    const host = String(url.hostname || '').toLowerCase();
    if (isLocalHostname(host)) return null;
    return `${url.origin}${NATURALS_ADMIN_PATH}`;
  } catch {
    return null;
  }
}

function isNaturalsSalonAdminDevOrigin(origin) {
  if (!origin) return false;
  try {
    const { hostname, port } = new URL(origin);
    if (!isLocalHostname(hostname)) return false;
    return port === '3000' || port === '3006' || port === '3008' || port === '3009' || port === '3015' || port === '3016' || port === '3017' || port === '5173' || port === '';
  } catch {
    return false;
  }
}

/** When the SPA is under /naturals/*, always same-origin Naturals salon-admin proxy. */
function apiBaseFromNaturalsPortalPath() {
  if (typeof window === 'undefined') return '';
  const path = String(window.location.pathname || '');
  if (!path.includes('/naturals/')) return '';
  const origin = window.location?.origin;
  if (!origin) return '';
  return `${origin.replace(/\/$/, '')}${NATURALS_ADMIN_PATH}`;
}

/** Resolved on each call (do not cache at module load). */
export function getNaturalsAdminApiBase() {
  return getSalonAdminApiBaseUrl().replace(/\/+$/, "");
}

export function getSalonAdminApiBaseUrl() {
  const fromPath = apiBaseFromNaturalsPortalPath();
  if (fromPath) return fromPath;

  const fromNaturalsEnv = normalizeApiBase(
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_NATURALS_SALON_ADMIN_API_URL
  );
  if (fromNaturalsEnv && !isWrongSalonAdminApi(fromNaturalsEnv)) {
    return fromNaturalsEnv;
  }

  if (typeof window !== 'undefined') {
    const fromWindow = normalizeApiBase(window.REACT_APP_NATURALS_SALON_ADMIN_API_URL);
    if (fromWindow && !isWrongSalonAdminApi(fromWindow)) {
      return fromWindow;
    }

    const origin = window.location && window.location.origin;
    if (origin) {
      const mapped = productionNaturalsApiOriginForHost(origin);
      if (mapped) return mapped;
    }

    if (isNaturalsSalonAdminDevOrigin(origin)) {
      return DEFAULT_DEV_API_ORIGIN;
    }
  }

  const fromSalonEnv = normalizeApiBase(
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_SALON_ADMIN_API_URL
  );
  if (fromSalonEnv && !isWrongSalonAdminApi(fromSalonEnv)) {
    return fromSalonEnv;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    const mapped = productionNaturalsApiOriginForHost(window.location.origin);
    if (mapped) return mapped;
  }

  return DEFAULT_DEV_API_ORIGIN;
}
